import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Chrome, RefreshCw, ShieldOff } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';

interface ExtensionDevice {
  id: string;
  label: string;
  tokenPrefix: string;
  extensionVersion: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

type DeviceState = 'active' | 'revoked' | 'expired';

function deviceState(d: ExtensionDevice): DeviceState {
  if (d.revokedAt) return 'revoked';
  if (d.expiresAt && new Date(d.expiresAt).getTime() <= Date.now()) return 'expired';
  return 'active';
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "3 days ago" style, falling back to a date once it stops being useful. */
function formatRelative(value: string | null): string {
  if (!value) return 'Never used';
  const diffMs = Date.now() - new Date(value).getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDate(value);
}

/**
 * Devices running the kol.my Insight extension for the signed-in user.
 *
 * Each connect mints one long-lived token; this is where it can be disconnected. It doubles as the
 * answer to "who on my team has actually installed the extension", which matters once captures
 * start feeding the shared creator pool.
 */
export default function ExtensionDevices() {
  const [devices, setDevices] = useState<ExtensionDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get<ExtensionDevice[]>('/auth/extension/devices');
      setDevices(Array.isArray(data) ? data : []);
    } catch {
      setError('Could not load your devices. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (device: ExtensionDevice) => {
    if (
      !window.confirm(
        `Disconnect "${device.label}"? The extension on that browser will stop working until it connects again.`
      )
    ) {
      return;
    }
    setRevokingId(device.id);
    setError(null);
    try {
      await api.delete(`/auth/extension/devices/${device.id}`);
      await load();
    } catch {
      setError('Could not disconnect that device. Please try again.');
    } finally {
      setRevokingId(null);
    }
  };

  const activeCount = devices.filter((d) => deviceState(d) === 'active').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Extension devices</h1>
          <p className="mt-1 text-sm text-glass-text-muted">
            Browsers signed in to the kol.my Insight extension with your account.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          Refresh
        </Button>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-[rgb(var(--danger)/0.4)] bg-[rgb(var(--danger)/0.1)] px-4 py-3 text-sm text-[rgb(var(--danger))]"
        >
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            Connected devices{!loading && activeCount > 0 ? ` (${activeCount} active)` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-6 text-sm text-glass-text-muted">Loading…</p>
          ) : devices.length === 0 ? (
            <div className="py-8 text-center">
              <Chrome className="mx-auto h-8 w-8 text-glass-text-muted" aria-hidden="true" />
              <p className="mt-3 text-sm text-glass-text-muted">
                No devices connected yet. Install the extension, then open{' '}
                <Link to="/extension/connect" className="text-accent2 hover:underline">
                  the connect page
                </Link>{' '}
                from its popup.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-glass text-left text-xs uppercase tracking-wide text-glass-text-muted">
                    <th className="px-3 py-2 font-medium">Device</th>
                    <th className="px-3 py-2 font-medium">Version</th>
                    <th className="px-3 py-2 font-medium">Last used</th>
                    <th className="px-3 py-2 font-medium">Connected</th>
                    <th className="px-3 py-2 font-medium">Expires</th>
                    <th className="px-3 py-2 font-medium sr-only">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => {
                    const state = deviceState(device);
                    return (
                      <tr key={device.id} className="border-b border-glass/50 last:border-0">
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{device.label}</span>
                            {state === 'revoked' && <Badge variant="secondary">Disconnected</Badge>}
                            {state === 'expired' && <Badge variant="warning">Expired</Badge>}
                          </div>
                          <span className="font-mono text-xs text-glass-text-muted">
                            {device.tokenPrefix}…
                          </span>
                        </td>
                        <td className="px-3 py-3 text-glass-text-muted">
                          {device.extensionVersion || '—'}
                        </td>
                        <td className="px-3 py-3 text-glass-text-muted">
                          {state === 'active' ? formatRelative(device.lastUsedAt) : '—'}
                        </td>
                        <td className="px-3 py-3 text-glass-text-muted">{formatDate(device.createdAt)}</td>
                        <td className="px-3 py-3 text-glass-text-muted">{formatDate(device.expiresAt)}</td>
                        <td className="px-3 py-3 text-right">
                          {state === 'active' && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => void revoke(device)}
                              disabled={revokingId === device.id}
                            >
                              <ShieldOff className="mr-2 h-4 w-4" aria-hidden="true" />
                              {revokingId === device.id ? 'Disconnecting…' : 'Disconnect'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How this works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-glass-text-muted">
          <p>
            Connecting the extension creates one token for that browser, valid for 180 days. Only you
            can see and disconnect your own devices.
          </p>
          <p>
            Disconnecting is immediate — the next action in that browser will ask you to connect again.
            Signing out everywhere from your profile also disconnects every device.
          </p>
          <p>
            The token is shown once, at connect time, and is never stored anywhere we can read it back.
            If you think one has leaked, disconnect it here and reconnect.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
